/**
 * Company Strategy - Queue-Driven Version
 * 
 * Algorithm:
 * 1. Create Company Suggestions queue item (Type 1):
 *    - If user messages exist: Use fetchChatCompletion with LLM prompt for company suggestions
 *    - If no messages: Use runMatchesAnalyze to get past companies for suggestions
 * 2. Create Company List Matches queue item (Type 2):
 *    - Look for queries with "Recent Company" in macro_taxonomy
 *    - Use runMultiMatches to get top 5 matches
 * 3. Add both queue items to central discovery queue
 * 4. Use MULTI_ROW_SELECTOR template so existing queue system handles rendering
 * 
 * Integrated with DirectTopicCallback system
 */

import { runMatchesAnalyze, runMultiMatches, fetchCopilotCompanyList, updateQueueItem } from '../api';
import { API_CONFIG } from '../../config/constants';
import { StrategyPhase } from './baseStrategy';
import centralDiscoveryQueue from '../centralDiscoveryQueue';
import { 
  DISCOVERY_TEMPLATE_TYPES 
} from '../discoveryQueueSchema';

/**
 * Helper function to create queue item from requirement 
 * Follows the same pattern as locationQueueStrategy.jsx createQueueItemFromRequirement
 */
const createQueueItemFromRequirement = async (requirement, isRecommended = false) => {
  const { section_name } = requirement;
  
  if (!section_name) {
    console.log('⚠️ No section_name, skipping requirement');
    return null;
  }
  
  const topic = 'companies';
  
  // Generate unique queue ID
  const queueId = `queue_${topic}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  // Use the bool_str as-is for companies
  let userFriendlyBoolStr = requirement.bool_str || '';
  let companyMapping = {};
  
  // Create the flat queue item structure expected by the UI
  const queueItem = {
    // Core fields expected by UI
    queue_id: queueId,
    section_id: requirement.section_id || `${topic}_${Date.now()}`,
    section_name: requirement.section_name,
    section_type: requirement.section_type || 'Nice to Have',
    section_order: requirement.section_order || (isRecommended ? 11 : 10),
    bool_str: userFriendlyBoolStr,
    topic: topic,
    disco_done: false,
    
    // Store original company data for reconstruction
    company_mapping: companyMapping,
    original_bool_str: requirement.bool_str,
    is_recommended: isRecommended,
    
    // Additional metadata for template system
    template_type: DISCOVERY_TEMPLATE_TYPES.MULTI_ROW_SELECTOR,
    ai_intro_message: isRecommended 
      ? `Based on your hiring history, here are AI-recommended similar companies where great candidates work 🤖\nSelect any companies you'd like to target! 🎯`
      : `Based on your past hiring, here are companies where you've successfully found candidates before 🏢\nSelect the companies you'd like to target for this role! 🎯`,
    ux_config: {
      title: '', // Empty to avoid duplication with ai_intro_message
      confirm_button_text: isRecommended ? 'Confirm Recommended Companies' : 'Confirm Companies',
      allow_empty_selection: true,
      ro_footer: false // Enable footer so users can type alternative company requirements
    },
    
    // Timestamps
    created_at: Date.now(),
    updated_at: Date.now()
  };
  
  return queueItem;
};


/**
 * Main company queue strategy function
 * @param {Object} decision - Current decision object from DirectTopicCallback
 * @param {Array} messages - Current conversation messages (empty when called from DirectTopicCallback)
 * @param {Object} queries - Queries object containing company queries
 * @returns {Object} Updated decision object
 */
const companyQueueStrategy = async (decision, messages, queries = {}) => {
  console.log('🏢 COMPANY QUEUE STRATEGY - Processing company topic');
  console.log('🏢 COMPANY QUEUE STRATEGY - decision object:', {
    sessionId: decision.sessionId,
    currentSessionId: decision.currentSessionId,
    userId: decision.userId,
    getAllRequirements: !!decision.getAllRequirements,
    allDecisionProps: Object.keys(decision),
    strategyPhase: decision.strategyPhase,
    hasMessages: messages && messages.length > 0
  });

  // Check if this is a user input scenario (has messages) vs initialization
  const hasUserInput = messages && messages.length > 0;
  const isInitialization = decision.strategyPhase === StrategyPhase.INIT;
  
  // If this is initialization AND there are already company items in the queue, skip to avoid duplicates
  if (isInitialization && !hasUserInput) {
    const existingCompanyItems = centralDiscoveryQueue.getItemsByTopic('companies');
    if (existingCompanyItems.length > 0) {
      console.log('🚫 COMPANY QUEUE STRATEGY: Initialization called but company items already exist, skipping to prevent duplicates');
      return {
        ...decision,
        actionType: 'CONTINUE_DISCOVERY',
        strategyPhase: StrategyPhase.CONVERSATION
      };
    }
  }
  
  try {
    let companyRequirements = [];

    // Step 1: Create Company Suggestions queue item (Type 1)
    let companySuggestions = [];
    let hasUserMessage = false;
    let icpRequirements = [];
    
    // Check if user provided messages for company suggestions
    if (messages && messages.length > 0) {
      console.log('🏢 COMPANY QUEUE STRATEGY: User provided messages, using chat completion for company suggestions');
      hasUserMessage = true;
      
      // Find existing recent_company_names item to update instead of delete+add
      console.log('🔄 COMPANY QUEUE STRATEGY: User input detected - will update existing recent_company_names item');
      const existingCompanyItems = centralDiscoveryQueue.getItemsByTopic('companies');
      const existingCompanyNamesItem = existingCompanyItems.find(item => item.section_id === 'recent_company_names');
      
      try {
        // Get the most recent user message
        const recentUserMessage = messages[messages.length - 1];
        if (recentUserMessage && (recentUserMessage.role === 'User' || recentUserMessage.role === 'user')) {
          console.log('🏢 COMPANY QUEUE STRATEGY: Processing most recent user message:', recentUserMessage.content);
          
          // Create company list description from the user's message  
          const userInput = recentUserMessage.content;
          
          console.log('🏢 COMPANY QUEUE STRATEGY: Using fetchCopilotCompanyList with user input:', userInput);
          
          const companyListResponse = await fetchCopilotCompanyList(userInput);
          console.log('🏢 COMPANY QUEUE STRATEGY: Got copilot company list response:', companyListResponse);
          
          // fetchCopilotCompanyList returns an array of company names directly
          if (Array.isArray(companyListResponse) && companyListResponse.length > 0) {
            companySuggestions = companyListResponse;
            console.log('🏢 COMPANY QUEUE STRATEGY: Extracted company suggestions:', companySuggestions.length);
            
            // IMMEDIATELY update existing recent_company_names item if it exists
            if (existingCompanyNamesItem) {
              const suggestedBoolStr = companySuggestions.map(company => 
                `workexp.recent.company.name:"${company}"`
              ).join(' OR ');

              const suggestedCompanyContext = companySuggestions.reduce((acc, company, index) => {
                acc[`suggested_${index}`] = company;
                return acc;
              }, {});

              const updatedItemData = {
                bool_str: `(${suggestedBoolStr})`,
                section_id: "recent_company_names",
                section_name: "Recent Companies",
                section_order: 1,
                section_type: "Nice to Have",
                topic: "companies",
                disco_done: false,
                template_type: DISCOVERY_TEMPLATE_TYPES.MULTI_ROW_SELECTOR,
                ai_intro_message: `Based on your input "${userInput}", here are the recommended companies to target 🎯\nSelect any companies you'd like to include in your search! 🏢`,
                ux_config: {
                  title: '', // Empty to avoid duplication with ai_intro_message
                  confirm_button_text: 'Confirm Selected Companies',
                  allow_empty_selection: true,
                  ro_footer: false // Enable footer so users can type alternative company requirements
                },
                req_cxt: {
                  company_cxt: suggestedCompanyContext
                }
              };
              
              console.log('🔄 COMPANY QUEUE STRATEGY: Updating existing recent_company_names item with new suggestions');
              const sessionId = decision.sessionId || decision.currentSessionId;
              const updateResult = await updateQueueItem(sessionId, existingCompanyNamesItem.queue_id, updatedItemData);
              
              if (updateResult) {
                console.log('✅ COMPANY QUEUE STRATEGY: Successfully updated existing recent_company_names item');
                // Update local queue to reflect changes immediately
                centralDiscoveryQueue.queue.set(existingCompanyNamesItem.queue_id, {
                  ...existingCompanyNamesItem,
                  ...updatedItemData,
                  updated_at: Date.now()
                });
                centralDiscoveryQueue.notifyListeners();
                
                // Early return since we've handled the user input
                return {
                  ...decision,
                  actionType: 'CONTINUE_DISCOVERY',
                  strategyPhase: StrategyPhase.CONVERSATION
                };
              } else {
                console.error('🚨 COMPANY QUEUE STRATEGY: Failed to update existing recent_company_names item, falling back to normal flow');
              }
            }
          } else {
            console.warn('🏢 COMPANY QUEUE STRATEGY: No companies returned from copilot API');
          }
        }
      } catch (apiError) {
        console.error('🏢 COMPANY QUEUE STRATEGY: Error with copilot company list API:', apiError);
        hasUserMessage = false;
      }
    }
    
    // If no user messages or API failed, use runMatchesAnalyze for company suggestions
    if (!hasUserMessage) {
      console.log('🏢 COMPANY QUEUE STRATEGY: No user messages or API failed, using matches analyze for suggestions');
      
      const allQueueItems = centralDiscoveryQueue.getAllItems();
      const completedItems = allQueueItems.filter(item => item.disco_done === true);
      
      if (completedItems.length > 0) {
        icpRequirements = completedItems.map(item => ({
          section_type: item.section_type || 'Must Have',
          section_name: item.section_name,
          section_id: item.section_id,
          bool_str: item.bool_str || ''
        }));
      } else if (decision.getAllRequirements) {
        const existingReqs = decision.getAllRequirements();
        if (existingReqs && existingReqs.length > 0) {
          icpRequirements = existingReqs;
        }
      }
      
      if (icpRequirements.length > 0) {
        try {
          const matchesAnalyzeResult = await runMatchesAnalyze(icpRequirements, {
            run_on_self_org: true
          });
          
          if (matchesAnalyzeResult?.analysis_info?.prev_comp_map) {
            const pastCompaniesMap = matchesAnalyzeResult.analysis_info.prev_comp_map;
            const companiesData = matchesAnalyzeResult.analysis_info.companies || {};
            
            const companyEntries = Object.entries(pastCompaniesMap).map(([companyId, profileIds]) => {
              const profileCount = Array.isArray(profileIds) ? profileIds.length : 0;
              const companyName = companiesData[companyId]?.name || `Company ${companyId}`;
              return { companyId, name: companyName, profileCount };
            });
            
            companySuggestions = companyEntries
              .sort((a, b) => b.profileCount - a.profileCount)
              .slice(0, 10)
              .map(c => c.name);
              
            console.log('🏢 COMPANY QUEUE STRATEGY: Found company suggestions from analysis:', companySuggestions.length);
          }
        } catch (analyzeError) {
          console.error('🏢 COMPANY QUEUE STRATEGY: Matches analyze failed:', analyzeError);
        }
      }
    }
    
    // Create Company Suggestions queue item (Type 1)
    if (companySuggestions.length > 0) {
      const suggestedBoolStr = companySuggestions.map(company => 
        `workexp.recent.company.name:"${company}"`
      ).join(' OR ');

      const suggestedCompanyContext = companySuggestions.reduce((acc, company, index) => {
        acc[`suggested_${index}`] = company;
        return acc;
      }, {});

      const companySuggestionsRequirement = {
        bool_str: `(${suggestedBoolStr})`,
        section_id: "recent_company_names",
        section_name: "Recent Companies",
        section_order: 1, // Ensure recent_company_names renders first
        section_type: "Nice to Have",
        req_cxt: {
          company_cxt: suggestedCompanyContext
        }
      };
      
      companyRequirements.push(companySuggestionsRequirement);
      console.log('🏢 COMPANY QUEUE STRATEGY: Created company suggestions requirement with', companySuggestions.length, 'companies');
    }

    // Step 2: Create Company List Matches queue item (Type 2)
    if (queries && Object.keys(queries).length > 0) {
      try {
        // Extract macro IDs that match Recent Company Lists criteria
        const recentCompanyMacroIds = Object.entries(queries)
          .filter(([, macro]) => {
            if (macro && macro.macro_taxonomy && Array.isArray(macro.macro_taxonomy)) {
              const taxonomy = macro.macro_taxonomy[0] ? macro.macro_taxonomy[0] : '';
              if ((taxonomy.indexOf('Company Lists') >= 0) && (taxonomy.indexOf('Recent') >= 0)) {
                return true;
              } 
              return false;
            }
            return false;
          })
          .map(([macroId]) => macroId); // Extract just the macro IDs
        
        if (recentCompanyMacroIds.length > 0) {
          console.log('🏢 COMPANY QUEUE STRATEGY: Found Recent Company macro IDs:', recentCompanyMacroIds);
          
          // Pass array of macro IDs directly to runMultiMatches
          const multiMatchesResult = await runMultiMatches(
            icpRequirements,
            recentCompanyMacroIds,
            [],
            { run_on_self_org:true }
          );
          
          if (multiMatchesResult && multiMatchesResult.count_by_queries) {
            // multiMatchesResult.count_by_queries is an object where keys are macro IDs and values are counts
            // Convert to array format for sorting
            const resultsArray = Object.entries(multiMatchesResult.count_by_queries)
              .map(([macroId, count]) => ({ macroId, count }))
              .filter(result => result.count > 0)
              .sort((a, b) => b.count - a.count)
              .slice(0, 10);
            
            console.log('🏢 COMPANY QUEUE STRATEGY: Top company categories by count:', resultsArray);
            
            if (resultsArray.length > 0) {
              // Build boolean string from top results using the macro IDs
              const companyQueryBoolStr = resultsArray.map(result => result.macroId).join(' OR ');
              
              // Create context mapping for UI display
              const companyQueryContext = resultsArray.reduce((acc, result, index) => {
                const queryName = queries[result.macroId]?.name || `Query ${index + 1}`;
                acc[`query_${index}`] = queryName;
                return acc;
              }, {});

              const recentCompaniesRequirement = {
                bool_str: `(${companyQueryBoolStr})`,
                section_id: "recent_company_lists", 
                section_name: "Recent Company Lists",
                section_order: 2, // Ensure recent_company_lists renders after recent_company_names
                section_type: "Nice to Have",
                req_cxt: {
                  company_cxt: companyQueryContext
                }
              };
              
              companyRequirements.push(recentCompaniesRequirement);
              console.log('🏢 COMPANY QUEUE STRATEGY: Created company categories requirement with', resultsArray.length, 'categories');
            }
          }
        }
      } catch (queryError) {
        console.error('🏢 COMPANY QUEUE STRATEGY: Error processing Recent Company queries:', queryError);
      }
    }
    
    // Step 3: Add requirements to central discovery queue
    const sessionId = decision.sessionId || decision.currentSessionId;
    if (sessionId && companyRequirements.length > 0) {
      await centralDiscoveryQueue.initialize(sessionId, []);
      
      // Add new requirements (user input scenario - items already cleared above)
      if (hasUserInput) {
        console.log('🏢 COMPANY QUEUE STRATEGY: Adding new requirements for user input scenario');
        for (const requirement of companyRequirements) {
          try {
            const isRecommended = requirement.section_name.includes('Recommended') || requirement.section_name.includes('Categories');
            const queueItem = await createQueueItemFromRequirement(requirement, isRecommended);
            if (queueItem) {
              await centralDiscoveryQueue.addQueueItem(queueItem);
              console.log('🏢 COMPANY QUEUE STRATEGY: Added new company queue item from user input:', requirement.section_name);
            }
          } catch (error) {
            console.error('🏢 COMPANY QUEUE STRATEGY: Error creating queue item:', error);
          }
        }
      } else {
        // No user messages - initialization scenario, just add new items
        for (const requirement of companyRequirements) {
          try {
            const isRecommended = requirement.section_name.includes('Recommended') || requirement.section_name.includes('Categories');
            const queueItem = await createQueueItemFromRequirement(requirement, isRecommended);
            if (queueItem) {
              await centralDiscoveryQueue.addQueueItem(queueItem);
              console.log('🏢 COMPANY QUEUE STRATEGY: Added initial company queue item:', requirement.section_name);
            }
          } catch (error) {
            console.error('🏢 COMPANY QUEUE STRATEGY: Error creating queue item:', error);
          }
        }
      }

      
    } else if (!sessionId) {
      console.log('❌ COMPANY QUEUE STRATEGY: No session ID available, cannot add to queue');
    } else {
      console.log('🏢 COMPANY QUEUE STRATEGY: No company requirements to add to queue');
    }
    
    // Step 4: Notify completion callback if provided
    if (decision.onQueuePopulated) {
      const totalCompanyItems = centralDiscoveryQueue.getItemsByTopic('companies').length;
      decision.onQueuePopulated(totalCompanyItems);
    }
    
    console.log('🏢 COMPANY QUEUE STRATEGY: Enhanced central discovery queue populated with', centralDiscoveryQueue.getAllItems().length, 'total items');
    
    return {
      ...decision,
      actionType: 'CONTINUE_DISCOVERY',
      strategyPhase: StrategyPhase.CONVERSATION
    };
    
  } catch (error) {
    console.error('🏢 COMPANY QUEUE STRATEGY: Error in company strategy:', error);
    
    return {
      ...decision,
      actionType: 'SEND_TO_BACKEND',
      agentGuidance: 'Let\'s discuss the company preferences for this role. Are there specific companies or types of companies you\'d like to target? For example, startups, established companies, or companies in particular industries?',
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
};

export default companyQueueStrategy;